import { useCallback, useEffect, useRef, useState } from 'react'
import { AppState, Platform } from 'react-native'
import {
  appUpdatePolicyService,
  type AppVersionPolicy,
} from '../services/app-update-policy.service'
import { isExpectedNonFatalOperationalError } from '../errors/service-error'
import { addBreadcrumb, captureError } from '../utils/observability'
import { isIsolatedPreviewBuild } from '../runtime-build'
import { env } from '../env'

export type MandatoryUpdateStatus = 'checking' | 'store-required' | 'error' | 'ready'

const POLICY_FETCH_TIMEOUT_MS = 8000

/**
 * Preview is a separate, fixture-only application identity. It must never
 * consult the production version-policy endpoint: an unavailable or stricter
 * production policy must not turn a local QA binary into an update wall.
 *
 * `isReviewDemoBuild` es el build de simulador que graba el video de App
 * Review: reporta CFBundleVersion 1 (la versión remota no se inyecta en
 * perfiles sin credenciales) y el muro bloquearía la grabación entera. Solo
 * ese perfil lo activa; producción y preview no lo conocen.
 */
function bypassRemoteVersionPolicy(): boolean {
  return Platform.OS === 'web' || __DEV__ || isIsolatedPreviewBuild || env.isReviewDemoBuild
}

export interface MandatoryUpdateGate {
  status: MandatoryUpdateStatus
  storeUrl: string | null
  currentBuildVersion: number
  requiredBuildVersion: number | null
  retry: () => void
  /**
   * EL TERMINO MEDIO QUE NO EXISTIA: hay una version mas nueva en la tienda y
   * NO es obligatoria.
   *
   * Hasta ahora esto solo tenia dos estados: `ready` o `store-required`, que es
   * un muro sin boton de escape montado antes del navegador. Y como el muro vive
   * apagado --y debe seguir apagado; encendido de mas ya encerro gente el
   * 23/08-- la app no hacia NADA con `latestBuildVersion`: quien estaba en un
   * build viejo nunca se enteraba de que existia uno nuevo.
   *
   * Eso importa mas de lo que parece, porque `runtimeVersion` es por huella
   * nativa: un build viejo vive en su propio silo y NINGUN hotfix por aire lo
   * alcanza. Callarse la actualizacion no era neutral, era dejar a esa persona
   * incomunicada de todo arreglo futuro.
   *
   * Con esto se avisa y se deja seguir. Forzar sin bloquear es exactamente esto:
   * decirlo, hacerlo un toque, y no secuestrar la pantalla.
   */
  updateAvailableBuildVersion: number | null
}

export function useMandatoryUpdateGate(): MandatoryUpdateGate {
  const [status, setStatus] = useState<MandatoryUpdateStatus>('ready')
  const [storeUrl, setStoreUrl] = useState<string | null>(null)
  const [requiredBuildVersion, setRequiredBuildVersion] = useState<number | null>(null)
  const [updateAvailableBuildVersion, setUpdateAvailableBuildVersion] = useState<number | null>(
    null,
  )
  const [retryNonce, setRetryNonce] = useState(0)
  const currentBuildVersion = appUpdatePolicyService.currentBuildVersion()
  const checking = useRef(false)

  const applyPolicy = useCallback(
    (policy: AppVersionPolicy): 'ready' | 'store-required' => {
      if (policy.enforceStoreUpdate) {
        const platformPolicy = Platform.OS === 'ios' ? policy.ios : policy.android
        const targetBuild = policy.forceLatestStoreBuild
          ? Math.max(platformPolicy.latestBuildVersion, platformPolicy.minBuildVersion)
          : platformPolicy.minBuildVersion

        if (targetBuild > 0 && currentBuildVersion <= 0) {
          throw new Error('Native build version unavailable for mandatory update policy')
        }

        if (targetBuild > 0 && currentBuildVersion < targetBuild) {
          setStoreUrl(platformPolicy.storeUrl)
          setRequiredBuildVersion(targetBuild)
          setStatus('store-required')
          return 'store-required'
        }
      }

      // No es obligatorio, pero SI hay algo mas nuevo: se guarda para avisar sin
      // bloquear. Se toma `latestBuildVersion` y no `minBuildVersion` porque el
      // minimo es el piso que se tolera, no lo que conviene tener.
      const platformPolicy = Platform.OS === 'ios' ? policy.ios : policy.android
      setUpdateAvailableBuildVersion(
        currentBuildVersion > 0 &&
          platformPolicy.latestBuildVersion > 0 &&
          currentBuildVersion < platformPolicy.latestBuildVersion
          ? platformPolicy.latestBuildVersion
          : null,
      )
      setStoreUrl(platformPolicy.storeUrl || null)
      setRequiredBuildVersion(null)
      setStatus('ready')
      return 'ready'
    },
    [currentBuildVersion],
  )

  const runCheck = useCallback(
    async (showChecking = false) => {
      if (bypassRemoteVersionPolicy()) {
        setStatus('ready')
        setStoreUrl(null)
        setRequiredBuildVersion(null)
        setUpdateAvailableBuildVersion(null)
        return
      }
      if (checking.current) return
      checking.current = true
      let warmCacheApplied = false

      const cachedPolicy = await appUpdatePolicyService.getCachedPolicy()
      if (cachedPolicy) {
        addBreadcrumb('updates', 'Using warm cached app version policy before remote refresh', {
          screen: 'root',
          action: 'mandatory-update-gate',
        })
        const cachedDecision = applyPolicy(cachedPolicy)
        warmCacheApplied = cachedDecision === 'ready'
      } else if (showChecking) {
        addBreadcrumb(
          'updates',
          'No cached app version policy; opening while remote policy loads',
          {
            screen: 'root',
            action: 'mandatory-update-gate',
          },
        )
      }

      try {
        // Startup must never download OTA updates. EAS updates are handled by
        // useOtaUpdate after the app is already usable.
        let policy: Awaited<ReturnType<typeof appUpdatePolicyService.fetchPolicy>>
        try {
          policy = await fetchPolicyWithTimeout()
        } catch (error) {
          const cachedPolicy = await appUpdatePolicyService.getCachedPolicy()
          if (cachedPolicy) {
            addBreadcrumb('updates', 'Using cached app version policy', {
              screen: 'root',
              action: 'mandatory-update-gate',
              message: error instanceof Error ? error.message : String(error),
            })
            policy = cachedPolicy
          } else if (!showChecking || warmCacheApplied) {
            addBreadcrumb('updates', 'Background app version policy refresh unavailable', {
              screen: 'root',
              action: 'mandatory-update-gate',
              message: error instanceof Error ? error.message : String(error),
            })
            if (!warmCacheApplied && !isExpectedNonFatalOperationalError(error)) {
              captureError(error, {
                screen: 'root',
                action: 'mandatory-update-gate-policy-unavailable',
              })
            }
            return
          } else {
            addBreadcrumb('updates', 'App version policy unavailable; opening with current build', {
              screen: 'root',
              action: 'mandatory-update-gate',
              message: error instanceof Error ? error.message : String(error),
            })
            if (!isExpectedNonFatalOperationalError(error)) {
              captureError(error, {
                screen: 'root',
                action: 'mandatory-update-gate-policy-unavailable',
              })
            }
            return
          }
        }

        applyPolicy(policy)
      } catch (error) {
        captureError(error, { screen: 'root', action: 'mandatory-update-gate' })
        setStatus('error')
      } finally {
        checking.current = false
      }
    },
    [applyPolicy],
  )

  useEffect(() => {
    void runCheck()
  }, [runCheck, retryNonce])

  useEffect(() => {
    if (bypassRemoteVersionPolicy()) return undefined
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') void runCheck(false)
    })
    return () => subscription.remove()
  }, [runCheck])

  const retry = useCallback(() => {
    setRetryNonce((value) => value + 1)
  }, [])

  return {
    status,
    storeUrl,
    currentBuildVersion,
    requiredBuildVersion,
    retry,
    updateAvailableBuildVersion,
  }
}

async function fetchPolicyWithTimeout(): Promise<
  Awaited<ReturnType<typeof appUpdatePolicyService.fetchPolicy>>
> {
  const controller = new AbortController()

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      // The old Promise.race rejected here but left the real request alive.
      // On a slow Android connection that request could finish later, race a
      // second startup check, and report the same timeout after the gate had
      // already recovered. Abort the actual fetch so one attempt has exactly
      // one terminal outcome.
      controller.abort()
      reject(new Error('App version policy request timed out'))
    }, POLICY_FETCH_TIMEOUT_MS)

    void appUpdatePolicyService.fetchPolicy(controller.signal).then(
      (policy) => {
        clearTimeout(timeout)
        resolve(policy)
      },
      (error) => {
        clearTimeout(timeout)
        reject(error)
      },
    )
  })
}
